import { Text } from '@thanx/uikit/text'
import { useGetEscalationQuery } from 'api/recoveryAi'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link, Redirect, RouteComponentProps } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.escalation_detail')

type Props = RouteComponentProps<{ id: string }>

// Transparent resolver for the escalation email deep-link
// (/recovery_ai/escalations/:id, keyed by escalation id): GET the linkage, then
// redirect to the case detail (keyed by conversation id), carrying the escalation
// id in router state so the case-detail block can wire acknowledge/resolve (F3
// Option A). This email deep-link is now the sole entry point — the browsable
// escalations queue was removed.
const EscalationDetail = ({ match }: Props) => {
  const [css] = useStyletron()
  // currentData (not data): keyed to the current :id, so a stale escalation never
  // leaks across ids — mirrors CaseDetail's route-change guard.
  const { currentData, isFetching, isUninitialized, isError } =
    useGetEscalationQuery(match.params.id)

  const notFound = (
    <div className="container py-xl">
      <Text.BodyText3 color="grey70">{t('not_found')}</Text.BodyText3>
      <div className={css({ marginTop: '12px' })}>
        <Link
          to="/recovery_ai/escalations"
          className={css({
            color: merchantTheme.colors.belizeHole50,
            fontWeight: 600,
            textDecoration: 'none',
          })}
        >
          {t('back')}
        </Link>
      </div>
    </div>
  )

  // 404 / missing / cross-merchant -> not-found. Never redirect on error: that
  // would bounce to a broken /recovery_ai/cases/undefined.
  if (isError) return notFound

  // Resolving the linkage -> spinner (covers first mount AND route-change
  // refetch). role="status" + a visually-hidden label announces the wait to
  // screen readers (the Spinner itself is icon-only).
  if (isUninitialized || isFetching || !currentData) {
    return (
      <div role="status" aria-live="polite">
        <span className="sr-only">{t('loading')}</span>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // Defense against BE contract drift (deploy-ordering: recovery-ai flag on before
  // the reshaped linkage ships in an env): a body without conversationId would
  // otherwise redirect to /cases/undefined. Degrade to the not-found state instead
  // — mirrors the isError guard above.
  if (!currentData.conversationId) return notFound

  // Resolved -> redirect to the case detail. Carry ONLY the escalation id in
  // router state: the case-detail block gates its buttons off the FRESH
  // conversation.escalation.status, so a router-state status would be stale.
  return (
    <Redirect
      to={{
        pathname: `/recovery_ai/cases/${currentData.conversationId}`,
        state: { escalationId: currentData.id },
      }}
    />
  )
}

export default EscalationDetail
